import { FC, useMemo, useState } from 'react';
import {
  PersonOffIcon,
  WbAlert,
  WbButton,
  WbNoData,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { ErrorPanel, Progress } from '@mainstage/core-components';
import { makeStyles } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { TeamRoleAssignment, TeamRoleAssignmentKindEnum } from '../../../types';
import { useTeamRoleAssignmentsColumns } from '../../../hooks';
import { TeamRoleEnum } from '@agilelab/plugin-wb-team-roles-common';
import { useTeamRolesPanelContext } from '../../../context';
import { OnboardBanner } from './OnboardBanner';
import { OnboardNoData } from './OnboardNoData';
import { DeleteTeamRoleAssignmentDialog } from '../DeleteTeamRoleAssignmentDialog';

const useStyles = makeStyles(theme => ({
  disabled: {
    background: theme.palette.grey[100],
    '&:hover': { background: theme.palette.grey[100] },
  },
  noDataIconContainer: {
    width: 140,
    height: 140,
    borderRadius: '50%',
    border: `2px dashed ${theme.palette.grey[300]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  noDataIcon: {
    width: 100,
    height: 100,
    display: 'block',
    color: theme.palette.info.main,
    opacity: 0.7,
  },
  noDataButton: {
    borderColor: theme.palette.info.main,
    color: theme.palette.white,
    backgroundColor: theme.palette.info.main,
    '&:hover': {
      backgroundColor: theme.palette.info.dark,
    },
  },
}));

export const TeamRoleAssignmentsTable: FC = () => {
  const classes = useStyles();
  const {
    selectedTeamRole,
    enabledTeamRoles,
    assignments,
    error,
    isLoading,
    setOpenCreateTeamRoleAssignmentDrawer,
  } = useTeamRolesPanelContext();

  const [assignmentToDelete, setAssignmentToDelete] =
    useState<TeamRoleAssignment | null>(null);

  const columns = useTeamRoleAssignmentsColumns({
    onDelete: assignment => setAssignmentToDelete(assignment),
  });

  const hasPrincipal = useMemo(
    () =>
      assignments?.some(a =>
        a.kinds.includes(TeamRoleAssignmentKindEnum.FULL),
      ) ?? false,
    [assignments],
  );

  const shouldShowAlert = useMemo(() => {
    return !hasPrincipal && (assignments?.length ?? 0) > 0;
  }, [hasPrincipal, assignments]);

  const currentTeamRole = useMemo(
    () => enabledTeamRoles.find(teamRole => teamRole.name === selectedTeamRole),
    [enabledTeamRoles, selectedTeamRole],
  );

  if (isLoading) return <Progress />;

  if (error) return <ErrorPanel error={error} />;

  return (
    <>
      {shouldShowAlert &&
        (selectedTeamRole === TeamRoleEnum.OWNER ? (
          <OnboardBanner />
        ) : (
          <WbAlert
            style={{ marginBottom: 16 }}
            variant="collapsible"
            message={{
              title: `No Full ${currentTeamRole?.displayName} assigned`,
              type: 'info',
              content: `There are only limited assignees or platform-assigned users for the ${currentTeamRole?.displayName} Team Role. Please assign a full assignee to ensure proper accountability.`,
            }}
          />
        ))}
      <WbTable<TeamRoleAssignment>
        noDataComponent={
          selectedTeamRole === TeamRoleEnum.OWNER ? (
            <OnboardNoData />
          ) : (
            <WbNoData
              fillContainer
              header={`No ${currentTeamRole?.displayName} assigned`}
              icon={
                <div className={classes.noDataIconContainer}>
                  <PersonOffIcon className={classes.noDataIcon} />
                </div>
              }
              text={`No ${currentTeamRole?.displayName} has been set yet. Related accountabilities are handed over to the Owner(s).`}
              primaryAction={
                <WbButton
                  variant="contained"
                  className={classes.noDataButton}
                  icon={<AddCircleOutlineIcon />}
                  label={`Assign ${currentTeamRole?.displayName}`}
                  onClick={() => setOpenCreateTeamRoleAssignmentDrawer(true)}
                />
              }
            />
          )
        }
        components={{
          tableLoader: {
            loading: isLoading,
          },
          tableContent: {
            columns,
            rows: assignments,
            rowDynamicClassName: row => {
              const hasFullOrLimited = row.kinds.some(
                k =>
                  k === TeamRoleAssignmentKindEnum.FULL ||
                  k === TeamRoleAssignmentKindEnum.LIMITED,
              );
              if (
                !hasFullOrLimited &&
                row.kinds.some(
                  k =>
                    k === TeamRoleAssignmentKindEnum.ASSIGNED_BY_THE_PLATFORM,
                )
              )
                return classes.disabled;
              return undefined;
            },
          },
        }}
      />
      <DeleteTeamRoleAssignmentDialog
        assignment={assignmentToDelete}
        onClose={() => setAssignmentToDelete(null)}
      />
    </>
  );
};
